import React, { useState, useEffect, useRef } from 'react';
import { messagesAPI, clientsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { 
  Mail, 
  MessageSquare, 
  Send, 
  RefreshCw,
  RefreshCcw,
  Inbox,
  Star,
  Archive,
  Trash2,
  Reply,
  Forward,
  Search,
  Plus,
  Link2,
  Unlink,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  ExternalLink,
  Paperclip,
  Download,
  FileText,
  Image,
  File
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper to get file icon based on mime type
const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.includes('pdf') || mimeType?.includes('document')) return FileText;
  return File;
};

// Safe HTML email renderer component
const EmailBody = ({ html, plainText }) => {
  const iframeRef = useRef(null);
  
  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        // Add base styles and render HTML
        const styledHtml = `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                font-size: 14px;
                line-height: 1.6;
                color: #1e293b;
                margin: 0;
                padding: 16px;
                background: white;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              a {
                color: #2563eb;
              }
              blockquote {
                border-left: 3px solid #e2e8f0;
                margin: 16px 0;
                padding-left: 16px;
                color: #64748b;
              }
              pre, code {
                background: #f1f5f9;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 13px;
              }
              table {
                border-collapse: collapse;
                max-width: 100%;
              }
              td, th {
                padding: 8px;
                border: 1px solid #e2e8f0;
              }
            </style>
          </head>
          <body>${html}</body>
          </html>
        `;
        doc.open();
        doc.write(styledHtml);
        doc.close();
        
        // Adjust iframe height to content
        setTimeout(() => {
          if (iframeRef.current && doc.body) {
            iframeRef.current.style.height = Math.max(doc.body.scrollHeight, 200) + 'px';
          }
        }, 100);
      }
    }
  }, [html]);
  
  if (!html && !plainText) {
    return <p className="text-slate-500 italic">No content</p>;
  }
  
  if (html) {
    return (
      <iframe
        ref={iframeRef}
        title="Email content"
        className="w-full border-0 min-h-[200px]"
        sandbox="allow-same-origin"
        style={{ height: '400px' }}
      />
    );
  }
  
  return <pre className="whitespace-pre-wrap text-sm text-slate-700">{plainText}</pre>;
};

export const Communication = () => {
  const [loading, setLoading] = useState(true);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('email');
  
  // Email state
  const [emailConnections, setEmailConnections] = useState({ gmail: null, outlook: null });
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailFolder, setEmailFolder] = useState('inbox');
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [isReplyMode, setIsReplyMode] = useState(false);
  
  // Client messages state
  const [portalMessages, setPortalMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [clients, setClients] = useState([]);
  const [messageSearchTerm, setMessageSearchTerm] = useState('');
  const [isReplyDialogOpen, setIsReplyDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [connectionsRes, messagesRes, clientsRes] = await Promise.all([
        api.get('/email/connections').catch(() => ({ data: { gmail: null, outlook: null } })),
        api.get('/portal-messages').catch(() => ({ data: [] })),
        clientsAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setEmailConnections(connectionsRes.data);
      setPortalMessages(messagesRes.data);
      setClients(clientsRes.data);
      
      // If any email is connected, fetch inbox
      if (connectionsRes.data?.gmail || connectionsRes.data?.outlook) {
        await fetchEmails();
      }
    } catch (error) {
      console.error('Failed to fetch communication data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmails = async (folder = 'inbox') => {
    setLoadingEmail(true);
    try {
      const response = await api.get(`/email/messages?folder=${folder}`);
      setEmails(response.data.messages || []);
      if (response.data.error) {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      setEmails([]);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleConnectEmail = async (provider) => {
    try {
      toast.info(`Initiating ${provider} connection...`);
      // Ensure we use HTTPS for the origin URL (required for OAuth)
      let originUrl = window.location.origin;
      if (originUrl.startsWith('http://') && !originUrl.includes('localhost')) {
        originUrl = originUrl.replace('http://', 'https://');
      }
      
      const response = await api.get(`/email/connect/${provider}?origin_url=${encodeURIComponent(originUrl)}`);
      if (response.data.auth_url) {
        // Redirect to OAuth provider
        window.location.href = response.data.auth_url;
      } else if (response.data.detail) {
        toast.error(response.data.detail);
      }
    } catch (error) {
      console.error(`Failed to connect ${provider}:`, error);
      const errorMessage = error.response?.data?.detail || `Failed to initiate ${provider} connection`;
      toast.error(errorMessage);
    }
  };

  const handleDisconnectEmail = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect your ${provider} account?`)) return;
    try {
      await api.delete(`/email/disconnect/${provider}`);
      toast.success(`${provider} account disconnected`);
      fetchData();
    } catch (error) {
      console.error(`Failed to disconnect ${provider}:`, error);
      toast.error(`Failed to disconnect ${provider}`);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/email/send', composeData);
      if (response.data.success) {
        toast.success(response.data.message || 'Email sent successfully');
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }
      setIsComposeOpen(false);
      setComposeData({ to: '', subject: '', body: '' });
      setIsReplyMode(false);
      fetchEmails(emailFolder);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
    }
  };

  const handleReplyToEmail = (email) => {
    const originalFrom = email.from_email?.includes('<') 
      ? email.from_email.match(/<(.+)>/)?.[1] || email.from_email 
      : email.from_email;
    
    setComposeData({
      to: originalFrom,
      subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n---\nOn ${formatDate(email.received_at)}, ${email.from_name || email.from_email} wrote:\n> ${email.body_preview}`
    });
    setIsReplyMode(true);
    setIsComposeOpen(true);
  };

  const handleDownloadAttachment = async (messageId, attachment) => {
    try {
      toast.info(`Downloading ${attachment.filename}...`);
      const response = await api.get(`/email/attachment/${messageId}/${attachment.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${attachment.filename}`);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleReplyToPortalMessage = async () => {
    if (!selectedMessage || !replyContent.trim()) return;
    try {
      await api.post('/portal-messages/reply', {
        message_id: selectedMessage.message_id,
        content: replyContent
      });
      toast.success('Reply sent successfully');
      setIsReplyDialogOpen(false);
      setReplyContent('');
      fetchData();
    } catch (error) {
      console.error('Failed to reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || 'Unknown Client';
  };

  const filteredEmails = emails.filter(email => 
    email.subject?.toLowerCase().includes(emailSearchTerm.toLowerCase()) ||
    email.from_name?.toLowerCase().includes(emailSearchTerm.toLowerCase()) ||
    email.from_email?.toLowerCase().includes(emailSearchTerm.toLowerCase())
  );

  const filteredMessages = portalMessages.filter(msg =>
    msg.subject?.toLowerCase().includes(messageSearchTerm.toLowerCase()) ||
    msg.content?.toLowerCase().includes(messageSearchTerm.toLowerCase())
  );

  const isEmailConnected = emailConnections?.gmail || emailConnections?.outlook;

  if (loading) {
    return <PageLoader text="Loading communication..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="communication-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Communication</h1>
          <p className="text-slate-500 mt-1">Manage emails and client portal messages</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="email" className="gap-2" data-testid="email-tab">
            <Mail className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="messages" className="gap-2" data-testid="messages-tab">
            <MessageSquare className="h-4 w-4" />
            Client Messages
          </TabsTrigger>
        </TabsList>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-6">
          {/* Email Connections Card */}
          <Card data-testid="email-connections-card">
            <CardHeader>
              <CardTitle className="text-lg">Connected Email Accounts</CardTitle>
              <CardDescription>Connect your email accounts to view and send emails directly from LEAP Pro</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Gmail Connection */}
                <div className={cn(
                  "p-4 rounded-lg border-2 transition-colors",
                  emailConnections?.gmail ? "border-green-200 bg-green-50" : "border-slate-200 hover:border-slate-300"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <svg className="h-6 w-6" viewBox="0 0 24 24">
                          <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Gmail</p>
                        {emailConnections?.gmail ? (
                          emailConnections.gmail.token_status === 'expired' ? (
                            <p className="text-sm text-amber-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Token expired - {emailConnections.gmail.email}
                            </p>
                          ) : (
                            <p className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Connected as {emailConnections.gmail.email}
                            </p>
                          )
                        ) : (
                          <p className="text-sm text-slate-500">Not connected</p>
                        )}
                      </div>
                    </div>
                    {emailConnections?.gmail ? (
                      <div className="flex items-center gap-2">
                        {emailConnections.gmail.token_status === 'expired' && (
                          <Button 
                            size="sm"
                            onClick={() => handleConnectEmail('gmail')}
                            data-testid="reconnect-gmail-btn"
                          >
                            <RefreshCcw className="h-4 w-4 mr-1" />
                            Reconnect
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDisconnectEmail('gmail')}
                          data-testid="disconnect-gmail-btn"
                        >
                          <Unlink className="h-4 w-4 mr-1" />
                          Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        size="sm"
                        onClick={() => handleConnectEmail('gmail')}
                        data-testid="connect-gmail-btn"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* Outlook Connection */}
                <div className={cn(
                  "p-4 rounded-lg border-2 transition-colors",
                  emailConnections?.outlook ? "border-green-200 bg-green-50" : "border-slate-200 hover:border-slate-300"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <svg className="h-6 w-6" viewBox="0 0 24 24">
                          <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.744.744 0 0 1-.55.234h-8.5V6.576h8.5c.213 0 .4.078.55.234.16.152.238.346.238.577zM14.713 17.57V6.576L8.212 5.18v14.836l6.501-2.446zM7.424 6.088H0v12.46h7.424V6.088z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Outlook</p>
                        {emailConnections?.outlook ? (
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Connected as {emailConnections.outlook.email}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-500">Not connected</p>
                        )}
                      </div>
                    </div>
                    {emailConnections?.outlook ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDisconnectEmail('outlook')}
                        data-testid="disconnect-outlook-btn"
                      >
                        <Unlink className="h-4 w-4 mr-1" />
                        Disconnect
                      </Button>
                    ) : (
                      <Button 
                        size="sm"
                        onClick={() => handleConnectEmail('outlook')}
                        data-testid="connect-outlook-btn"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {!isEmailConnected && (
                <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Email Integration Required</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Connect your Gmail or Outlook account to view, send, and reply to emails directly from LEAP Pro.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Interface */}
          {isEmailConnected && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Email List */}
              <Card className="lg:col-span-1" data-testid="email-list-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Inbox</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => fetchEmails(emailFolder)}
                        disabled={loadingEmail}
                        data-testid="refresh-emails-btn"
                      >
                        <RefreshCw className={cn("h-4 w-4", loadingEmail && "animate-spin")} />
                      </Button>
                      <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" data-testid="compose-email-btn">
                            <Plus className="h-4 w-4 mr-1" />
                            Compose
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[600px]">
                          <DialogHeader>
                            <DialogTitle>{isReplyMode ? 'Reply to Email' : 'New Email'}</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleSendEmail} className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor="to">To *</Label>
                              <Input
                                id="to"
                                type="email"
                                value={composeData.to}
                                onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                                placeholder="recipient@example.com"
                                required
                                data-testid="email-to-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="subject">Subject *</Label>
                              <Input
                                id="subject"
                                value={composeData.subject}
                                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                                placeholder="Email subject"
                                required
                                data-testid="email-subject-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="body">Message *</Label>
                              <Textarea
                                id="body"
                                value={composeData.body}
                                onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                                rows={10}
                                required
                                data-testid="email-body-input"
                              />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                              <Button type="button" variant="outline" onClick={() => {
                                setIsComposeOpen(false);
                                setIsReplyMode(false);
                                setComposeData({ to: '', subject: '', body: '' });
                              }}>
                                Cancel
                              </Button>
                              <Button type="submit" data-testid="send-email-btn">
                                <Send className="h-4 w-4 mr-2" />
                                Send
                              </Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <div className="relative mt-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search emails..."
                      value={emailSearchTerm}
                      onChange={(e) => setEmailSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-emails-input"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  {loadingEmail ? (
                    <div className="text-center py-12 text-slate-500">
                      <RefreshCw className="h-8 w-8 mx-auto animate-spin text-slate-300 mb-3" />
                      <p>Loading emails...</p>
                    </div>
                  ) : filteredEmails.length > 0 ? (
                    <div className="divide-y">
                      {filteredEmails.map((email) => (
                        <div
                          key={email.id}
                          onClick={() => setSelectedEmail(email)}
                          className={cn(
                            "p-4 cursor-pointer hover:bg-slate-50 transition-colors",
                            selectedEmail?.id === email.id && "bg-blue-50",
                            !email.is_read && "bg-blue-50/50"
                          )}
                          data-testid={`email-item-${email.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className={cn(
                                  "text-sm truncate",
                                  !email.is_read ? "font-semibold text-slate-900" : "text-slate-700"
                                )}>
                                  {email.from_name || email.from_email}
                                </p>
                                {email.has_attachments && (
                                  <Paperclip className="h-3 w-3 text-slate-400 flex-shrink-0" />
                                )}
                              </div>
                              <p className={cn(
                                "text-sm truncate mt-0.5",
                                !email.is_read ? "font-medium text-slate-800" : "text-slate-600"
                              )}>
                                {email.subject}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-1">
                                {email.body_preview}
                              </p>
                            </div>
                            <div className="text-xs text-slate-400 whitespace-nowrap">
                              {formatDate(email.received_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p>No emails found</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Email Preview */}
              <Card className="lg:col-span-2" data-testid="email-preview-card">
                {selectedEmail ? (
                  <>
                    <CardHeader className="pb-3 border-b">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg">{selectedEmail.subject}</CardTitle>
                          <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{selectedEmail.from_name || 'Unknown'}</span>
                            <span className="text-slate-400">&lt;{selectedEmail.from_email?.match(/<(.+)>/)?.[1] || selectedEmail.from_email}&gt;</span>
                          </div>
                          {selectedEmail.to && (
                            <div className="text-sm text-slate-500 mt-1">
                              To: {selectedEmail.to}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(selectedEmail.received_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleReplyToEmail(selectedEmail)}
                            data-testid="reply-email-btn"
                          >
                            <Reply className="h-4 w-4 mr-1" />
                            Reply
                          </Button>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                            <Paperclip className="h-4 w-4" />
                            <span>{selectedEmail.attachments.length} Attachment{selectedEmail.attachments.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedEmail.attachments.map((attachment, idx) => {
                              const FileIcon = getFileIcon(attachment.mimeType);
                              return (
                                <button
                                  key={idx}
                                  onClick={() => handleDownloadAttachment(selectedEmail.id, attachment)}
                                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                                  data-testid={`download-attachment-${idx}`}
                                >
                                  <FileIcon className="h-4 w-4 text-slate-500 group-hover:text-blue-600" />
                                  <div className="text-left">
                                    <p className="text-sm font-medium text-slate-700 group-hover:text-blue-700 max-w-[150px] truncate">
                                      {attachment.filename}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      {formatFileSize(attachment.size)}
                                    </p>
                                  </div>
                                  <Download className="h-4 w-4 text-slate-400 group-hover:text-blue-600" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-[500px] overflow-y-auto">
                        <EmailBody 
                          html={selectedEmail.body_html} 
                          plainText={selectedEmail.body_preview}
                        />
                      </div>
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="flex items-center justify-center h-[500px]">
                    <div className="text-center text-slate-500">
                      <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p>Select an email to view</p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Client Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <Card className="lg:col-span-1" data-testid="portal-messages-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Client Portal Messages</CardTitle>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search messages..."
                    value={messageSearchTerm}
                    onChange={(e) => setMessageSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-messages-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                {filteredMessages.length > 0 ? (
                  <div className="divide-y">
                    {filteredMessages.map((msg) => (
                      <div
                        key={msg.message_id}
                        onClick={() => setSelectedMessage(msg)}
                        className={cn(
                          "p-4 cursor-pointer hover:bg-slate-50 transition-colors",
                          selectedMessage?.message_id === msg.message_id && "bg-blue-50",
                          !msg.read_at && "bg-blue-50/50"
                        )}
                        data-testid={`portal-message-${msg.message_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className={cn(
                              "text-sm truncate",
                              !msg.read_at ? "font-semibold text-slate-900" : "text-slate-700"
                            )}>
                              {getClientName(msg.sender_id)}
                            </p>
                            <p className={cn(
                              "text-sm truncate mt-0.5",
                              !msg.read_at ? "font-medium text-slate-800" : "text-slate-600"
                            )}>
                              {msg.subject}
                            </p>
                            <p className="text-xs text-slate-500 truncate mt-1">
                              {msg.content?.substring(0, 80)}...
                            </p>
                          </div>
                          <div className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDate(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No messages from clients</p>
                    <p className="text-xs mt-1">Messages from your client portal will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Message Preview */}
            <Card className="lg:col-span-2" data-testid="message-preview-card">
              {selectedMessage ? (
                <>
                  <CardHeader className="pb-3 border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{selectedMessage.subject}</CardTitle>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                          <User className="h-4 w-4" />
                          <span>{getClientName(selectedMessage.sender_id)}</span>
                          <span className="text-slate-400">•</span>
                          <Clock className="h-4 w-4" />
                          <span>{formatDate(selectedMessage.created_at)}</span>
                        </div>
                        {selectedMessage.case_id && (
                          <Badge variant="outline" className="mt-2">
                            Case Related
                          </Badge>
                        )}
                      </div>
                      <Dialog open={isReplyDialogOpen} onOpenChange={setIsReplyDialogOpen}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid="reply-portal-message-btn"
                          >
                            <Reply className="h-4 w-4 mr-1" />
                            Reply
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Reply to {getClientName(selectedMessage.sender_id)}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div className="p-3 rounded-lg bg-slate-50 border">
                              <p className="text-sm text-slate-500 mb-1">Original message:</p>
                              <p className="text-sm">{selectedMessage.content?.substring(0, 200)}...</p>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="reply">Your Reply</Label>
                              <Textarea
                                id="reply"
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                rows={6}
                                placeholder="Type your reply..."
                                data-testid="portal-reply-input"
                              />
                            </div>
                            <div className="flex justify-end gap-3">
                              <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => {
                                  setIsReplyDialogOpen(false);
                                  setReplyContent('');
                                }}
                              >
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleReplyToPortalMessage}
                                data-testid="send-portal-reply-btn"
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="prose prose-slate max-w-none">
                      <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                    </div>
                    
                    {/* Thread / Replies */}
                    {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                      <div className="mt-6 pt-6 border-t">
                        <h4 className="font-medium text-slate-900 mb-4">Conversation Thread</h4>
                        <div className="space-y-4">
                          {selectedMessage.replies.map((reply, idx) => (
                            <div key={idx} className="p-4 rounded-lg bg-slate-50 border">
                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                <User className="h-4 w-4" />
                                <span>{reply.sender_name}</span>
                                <span className="text-slate-400">•</span>
                                <span>{formatDate(reply.created_at)}</span>
                              </div>
                              <p className="text-sm">{reply.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <CardContent className="flex items-center justify-center h-[400px]">
                  <div className="text-center text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>Select a message to view</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Communication;
